import { TreeNode } from "@/types/tree";
import { getSingularitySectionLibrary } from "./library-singleton";
import { uiStore, documentStore } from "@/store/store";
import { pasteElements } from "../design-library/design-library";
import { addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead } from "../canvas/assets/css-in-head";
import { getBuilderCanvasDocument } from "../canvas/helpers";
import {
  addFriendlyNameToSection,
  addPromptInfoToSection
} from "@/lib/singularity/singularity";
import { findNode, getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import { aiJobController } from "@/futurelayer/ai-job-manager/ai-jobs";
import { rewriteContentInSection } from "@/lib/singularity/sections";
import { replaceImagesInSectionWithAi } from "@/futurelayer/images/main";
import { getPromptInfoFromNode } from "@/lib/singularity/util";
import { forceNextTick } from "@/util/force-next-tick";
import type SingularityModule from "@/store/modules/singularity";
import { FUTURELAYER_HEADER_ELEMENT_TYPE } from "@/futurelayer/headers-footers/headers-footers";
import { extractExistingContentFromSection } from "@/lib/ai/ai";

const SCREENSHOT_PATH =
  "https://breakdance-design-library-section-screenshots-ap-production.up.railway.app/screenshots/barebones";

export function getSectionsByType(sectionType: string): SectionCardData[] {
  const sectionLibrary = getSingularitySectionLibrary();
  if (!sectionLibrary[sectionType]) return [];
  return sectionLibrary[sectionType].map((section, i) => {
    return {
      label: `${sectionType} ${i}`,
      index: i,
      node: section,
      type: sectionType,
      screenshotUrl: getScreenshotUrlForSection(sectionType, String(section.id))
    };
  });
}

export function getAllSections(): SectionCardData[] {
  const sectionLibrary = getSingularitySectionLibrary();
  return Object.entries(sectionLibrary).flatMap(([sectionType, sections]) => {
    return sections.map((section, i) => {
      return {
        label: `${sectionType} ${i}`,
        index: i,
        node: section,
        type: sectionType,
        screenshotUrl: getScreenshotUrlForSection(
          sectionType,
          String(section.id)
        )
      };
    });
  });
}

export function getAllSectionsCategory(): SectionCardData[] {
  const sectionLibrary = getSingularitySectionLibrary();
  return Object.entries(sectionLibrary)
    .map(([sectionType, sections]) => {
      const section = sections[0];
      return {
        label: `${sectionType} 0`,
        index: 0,
        node: section,
        type: sectionType,
        screenshotUrl: getScreenshotUrlForSection(
          sectionType,
          String(section.id)
        )
      };
    })
    .filter(Boolean);
}

function getIndexToInsertForPotentiallyBlankPage(): number {
  const headerElement = documentStore.document.tree.root.children.find(
    child => child.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
  );
  return headerElement ? 1 : 0;
}

export function insertSectionAfterNode(
  section: TreeNode,
  afterNode: TreeNode["id"] | -1
): TreeNode["id"] | null {
  let parentNode: TreeNode["id"];
  let index: number;

  if (afterNode === -1) {
    parentNode = documentStore.document.tree.root.id;
    index = getIndexToInsertForPotentiallyBlankPage();
  } else {
    const parentNodeObj = getNodeParent(documentStore.document.tree, afterNode);
    if (parentNodeObj === false) return null;

    parentNode = parentNodeObj.id;
    const afterNodeIndex = getNodeIndex(documentStore.document.tree, afterNode);
    index = afterNodeIndex !== false ? afterNodeIndex + 1 : 0;
  }

  const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
    nodeId: undefined
  };

  pasteElements([section], "inserting", pointerForCreatedNodeId);
  if (pointerForCreatedNodeId.nodeId) {
    documentStore.moveElement({
      draggedElementId: pointerForCreatedNodeId.nodeId,
      droppedOnElementId: parentNode,
      index: index
    });
  }

  return pointerForCreatedNodeId.nodeId ?? null;
}

export function temporarilyHideSectionOnCanvas(nodeId: TreeNode["id"]) {
  addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead(
    getBuilderCanvasDocument(),
    `section-add-or-swap-panel-temporary-canvas-styles`,
    `
    [data-node-id="${nodeId}"] {
      display: none !important;
    }
    `
  );
}

export function unhideTemporarilyHiddenSection() {
  addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead(
    getBuilderCanvasDocument(),
    `section-add-or-swap-panel-temporary-canvas-styles`,
    ``
  );
}

export type SectionCardData = {
  label: string;
  index: number;
  node: TreeNode;
  type: string;
  screenshotUrl: string;
};

export function getScreenshotUrlForSection(
  pageSlug: string,
  sectionId: string | number
): string {
  const cleanPageSlug = pageSlug.toLowerCase().replace(/[^a-z0-9]/g, "");
  const filename = `${cleanPageSlug}-${sectionId}.jpg`;
  return `${SCREENSHOT_PATH}/${cleanPageSlug}/${filename}`;
}

export function handleSectionChosen(
  section: SectionCardData,
  index: number,
  sectionId: TreeNode["id"],
  mode: SingularityModule["sectionAddOrSwapPanelState"]["mode"],
  rewriteWithAiOnAdd: boolean
) {
  let existingContentFromOldSection: string | undefined = undefined;
  if (mode === "swap") {
    const oldSection = findNode(documentStore.document.tree, sectionId);
    if (oldSection) {
      existingContentFromOldSection = extractExistingContentFromSection(oldSection);
    }
  }

  const sectionDescription = {
    type: section.type,
    sectionFriendlyName: `${section.type}`,
    description: ""
  };

  const sectionWithPrompt = addPromptInfoToSection(
    section.node,
    sectionDescription,
    index,
    false
  );

  const sectionWithFriendlyName = addFriendlyNameToSection(
    sectionWithPrompt,
    sectionDescription.sectionFriendlyName
  );

  const currentSectionId = insertSectionAfterNode(
    sectionWithFriendlyName,
    sectionId
  );

  if (currentSectionId) {
    const sectionId = currentSectionId;
    const section = findNode(documentStore.document.tree, sectionId);
    const sectionIndex = getNodeIndex(documentStore.document.tree, sectionId);
    if (!section) return;

    if (mode === "add_before") {
      documentStore.moveElement({
        draggedElementId: currentSectionId,
        droppedOnElementId: documentStore.document.tree.root.id,
        index: sectionIndex ? Math.max(0, sectionIndex - 1) : 0
      });
    }

    if (rewriteWithAiOnAdd) {
      aiJobController.addJob(
        async abortController => {
          await rewriteContentInSection(sectionId, abortController, true, existingContentFromOldSection);
          await replaceImagesInSectionWithAi(section, abortController);
        },
        {
          jobType: 'section_rewrite',
          documentId: documentStore.document.documentMeta.id,
          elementId: sectionId,
          description: `Rewrite and image population for new section in document "${documentStore.document.documentMeta.titleLabel}" - section ${sectionId}`,
          priority: 'critical',
          snapshotLabel: 'Rewrite section with AI'
        }
      );
    }
  }

  if (mode === "swap") {
    documentStore.deleteElement({
      nodeId: sectionId
    });
  }

  /*
  documentStore.deleteElement uses forceNextTick, so the element will actually be 
  deleted after uiStore.activateElement runs if we don't use forceNextTick here,
  and deleting an element activates the parent node
  */
  forceNextTick(() => {
    uiStore.activateElement(currentSectionId);
  });
}

export function getSectionCardDataFromNode(
  id: TreeNode["id"]
): SectionCardData | null {
  const promptInfo = getPromptInfoFromNode(
    findNode(documentStore.document.tree, id)!
  );

  if (
    !promptInfo.singularity ||
    !promptInfo.singularity.sectionData.type ||
    !promptInfo.singularity.sectionData.index
  )
    return null;

  const sectionType = promptInfo.singularity.sectionData.type;
  const sectionIndex = promptInfo.singularity.sectionData.index;

  return (
    getAllSections().find(
      section => section.type === sectionType && section.index === sectionIndex
    ) || null
  );
}
